import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api, apiError } from "@/lib/api";

const Stars = ({ value, onChange, size = 18, testPrefix }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={testPrefix ? `${testPrefix}-star-${n}` : undefined}
        onClick={onChange ? () => onChange(n) : undefined}
        className={onChange ? "cursor-pointer" : "cursor-default"}
        tabIndex={onChange ? 0 : -1}
      >
        <Star size={size} className={n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
      </button>
    ))}
  </div>
);

export const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 0, comment: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.rating) return toast.error("Please select a star rating");
    if (!form.comment.trim()) return toast.error("Please write a short review");
    setBusy(true);
    try {
      await api.post("/reviews", form);
      setForm({ name: "", rating: 0, comment: "" });
      toast.success("Thank you! Your review will appear once it's approved. 🌴");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16 lg:py-24">
      <p className="text-cyan-600 font-semibold tracking-[0.2em] uppercase text-xs mb-3">Guest Reviews</p>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-10">
        What our guests say
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {reviews.length === 0 ? (
            <p className="text-slate-500 text-sm bg-white border border-slate-200 rounded-2xl p-6">
              No reviews yet — be the first to share your experience!
            </p>
          ) : (
            <div data-testid="reviews-list" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {reviews.slice(0, 6).map((r) => (
                <div key={r.id} data-testid={`review-card-${r.id}`} className="bg-white border border-cyan-100 rounded-2xl p-6 shadow-sm">
                  <Stars value={r.rating} size={15} />
                  <p className="text-sm text-slate-600 leading-relaxed mt-3 mb-4">"{r.comment}"</p>
                  <p className="text-sm font-bold text-slate-900">{r.name}</p>
                  <p className="text-xs text-slate-400">
                    {new Date(r.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
        <form onSubmit={submit} data-testid="review-form" className="bg-[#071426] rounded-3xl p-7 h-fit">
          <h3 className="font-display text-lg font-bold text-white mb-4">Leave a review</h3>
          <input
            data-testid="review-name-input"
            className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-cyan-400"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="mb-3">
            <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={26} testPrefix="review" />
          </div>
          <textarea
            data-testid="review-comment-input"
            rows={4}
            className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-xl px-4 py-3 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-400 resize-none"
            placeholder="How was your trip?"
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
          />
          <button data-testid="review-submit-button" type="submit" disabled={busy} className="btn-coral w-full text-white font-bold py-3 rounded-full disabled:opacity-60">
            {busy ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      </div>
    </section>
  );
};
